import React from 'react';
import { Row, Col, Divider } from 'antd';

import {
  GroupElementWrapper,
  HeaderGroupElement,
  PropertyBoolean,
  PropertyList,
  PropertyNumber,
  PropertyText,
} from 'pages/product-full-view/shared/components';
import { formatMDY } from 'utils/formatDate';

const NUMERIC_TYPE = ['number', 'integer', 'decimal'];

const DisplayWalmartInfo = (props) => {
  const { enrichedData } = props;

  const productData = enrichedData?.data;

  if (!productData) return null;

  return (
    <Row style={{ position: 'relative', marginTop: 6, marginLeft: 6 }}>
      <Col span={24}>
        {Object.keys(productData).map((topLevelKey) => {
          const schemaProperty = productData[topLevelKey];

          return (
            <TopLevelProperty
              key={topLevelKey}
              schemaProperty={schemaProperty}
            />
          );
        })}
      </Col>
    </Row>
  );
};

const TopLevelProperty = ({ schemaProperty }) => {
  const { title, childProperties } = schemaProperty;

  if (!childProperties || childProperties.length === 0) return null;

  const validChildProperties = childProperties.filter(
    (prop) => prop.value !== undefined
  );

  if (validChildProperties.length === 0) return null;

  return (
    <Row className='product-detail__property-container'>
      <GroupElementWrapper span={24} style={{ width: '100%', marginTop: 12 }}>
        <HeaderGroupElement header={title} style={{ marginTop: -4 }} />
        <Col span={24} style={{ marginLeft: 12, padding: '12px 0px' }}>
          {validChildProperties.map((property) => (
            <ProductPropertyItem key={property.fullKey} property={property} />
          ))}
        </Col>
      </GroupElementWrapper>
    </Row>
  );
};

const ProductPropertyItem = ({ property }) => {
  const { childProperties, type, title, value, enums, fullKey, format } =
    property;

  if (value === undefined && !childProperties) return null;

  const propertyProps = {
    property: { propertyDisplayName: title, fieldFullPath: fullKey },
    data: { [fullKey]: value },
  };

  if (type === 'object' && childProperties && childProperties.length > 0) {
    const validChildren = childProperties.filter(
      (child) => child.value !== undefined
    );

    if (validChildren.length === 0) return null;

    return (
      <Row style={{ marginTop: 12, marginRight: 12 }}>
        <GroupElementWrapper
          span={24}
          style={{ padding: '8px 0px', marginTop: 8 }}
        >
          <HeaderGroupElement
            className='product-detail__group-header'
            header={title}
          />
          <div style={{ marginLeft: 12 }}>
            {validChildren.map((childProp) => (
              <ProductPropertyItem
                key={childProp.fullKey}
                property={childProp}
              />
            ))}
          </div>
        </GroupElementWrapper>
      </Row>
    );
  }

  if (type === 'array') {
    return <ArrayPropertyItem property={property} />;
  }

  if (type === 'string') {
    if (format === 'date-time')
      return (
        <PropertyText
          {...propertyProps}
          data={{ [fullKey]: formatMDY(value) }}
        />
      );

    return <PropertyText {...propertyProps} />;
  }

  if (NUMERIC_TYPE.includes(type)) {
    return <PropertyNumber {...propertyProps} />;
  }

  if (type === 'true/false') {
    return <PropertyBoolean {...propertyProps} />;
  }

  return <PropertyText {...propertyProps} />;
};

const ArrayPropertyItem = ({ property }) => {
  const { value, items, title, fullKey } = property;

  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  if (items && items.childProperties && items.childProperties.length > 0) {
    return (
      <Row style={{ marginTop: 12, marginRight: 12 }}>
        <GroupElementWrapper
          span={24}
          style={{ padding: '8px 0px', marginTop: 8 }}
        >
          <HeaderGroupElement
            className='product-detail__group-header'
            header={title}
          />
          {value.map((itemValue, index) => {
            const lastIndex = index === value.length - 1;

            const itemChildProps = items.childProperties[index];

            if (!itemChildProps) return null;

            return (
              <React.Fragment key={index}>
                <div style={{ marginLeft: 12, marginTop: 8 }}>
                  {itemChildProps.map((childProp) => (
                    <ProductPropertyItem
                      key={childProp.fullKey}
                      property={childProp}
                    />
                  ))}
                </div>
                {!lastIndex && (
                  <Divider
                    style={{
                      margin: '12px 8px',
                      minWidth: '96%',
                      width: '96%',
                      borderTopColor: '#f0f0f0',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </GroupElementWrapper>
      </Row>
    );
  }

  return (
    <PropertyList
      property={{ propertyDisplayName: title, fieldFullPath: fullKey }}
      data={{ [fullKey]: value }}
    />
  );
};

export default DisplayWalmartInfo;
